import * as React from 'react';
import { Plus } from 'lucide-react';
import {
  BottomSheet,
  BottomSheetClose,
  BottomSheetContent,
} from './BottomSheet';
import { Avatar } from './Avatar';
import { Button } from './Button';
import { Checkbox } from './Checkbox';
import { EmptyState } from './EmptyState';
import { IconTile } from './Icon';
import { Inline, Stack } from './Layout';
import { ListRow, type ListRowProps } from './List';
import { Text } from './Text';

/**
 * `as="button"` for a row that is itself the thing to tap, opening the
 * person; a row holding a control of its own, a checkbox, stays a div.
 */
export interface MemberRowProps extends Omit<ListRowProps, 'children'> {
  name: string;
  avatarSrc?: string | null;
  /** A line under the name: "On duty · Truck 4", "Owner". */
  detail?: React.ReactNode;
  /** On the right: a checkbox, a badge, a button. */
  control?: React.ReactNode;
  /**
   * `lg` is a row in a sheet you pick people from, as the Operators sheet
   * draws it: the name at 20px, 8 from the avatar. `md` is a list of
   * people, with room for the line under the name.
   */
  size?: 'md' | 'lg';
}

/**
 * A person in a list: their avatar, name and a line about them, and a
 * control on the right. The operator picker's rows, and any list of
 * members a screen needs: the mobile app's drivers are these as buttons,
 * their duty status on the right.
 *
 *   <MemberRow name="Marcus Webb" detail="On duty · Truck 4"
 *     control={<Checkbox checked={on} aria-label="Marcus Webb" />} />
 */
export const MemberRow = React.forwardRef<HTMLElement, MemberRowProps>(
  function MemberRow(
    { name, avatarSrc, detail, control, inset = 'y', size = 'md', ...props },
    ref
  ) {
    const who = (
      <Stack gap={0} grow>
        <Text
          as="span"
          variant={size === 'lg' ? 'h2' : 'body-lg-strong'}
          truncate
        >
          {name}
        </Text>
        {detail ? (
          <Text as="span" variant="body-sm" tone="muted" truncate>
            {detail}
          </Text>
        ) : null}
      </Stack>
    );
    return (
      <ListRow ref={ref} inset={inset} {...props}>
        {size === 'lg' ? (
          <Inline gap={2} align="center" grow>
            <Avatar name={name} src={avatarSrc} size="sm" />
            {who}
          </Inline>
        ) : (
          <>
            <Avatar name={name} src={avatarSrc} size="sm" />
            {who}
          </>
        )}
        {control}
      </ListRow>
    );
  }
);

export interface Operator {
  id: string;
  name: string;
  avatarSrc?: string | null;
  /** "On duty · Truck 4". */
  detail?: React.ReactNode;
}

export interface OperatorPickerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Who can be put on the call, in the order to offer them. */
  operators: Operator[];
  /** The ids on the call now. */
  selected: string[];
  /** Called once, on Done, with the ids ticked in the sheet. */
  onSelectedChange: (ids: string[]) => void;
  /**
   * Draws "Add new operator" at the foot of the list, for a member allowed
   * to invite one. Left out, the row is not there.
   */
  onAddOperator?: () => void;
  title?: string;
}

/**
 * Who is on a call: everyone who can be, with a tick against each one on it.
 * The creating flow's operator sheet.
 *
 *   <OperatorPicker open={open} onOpenChange={setOpen}
 *     operators={drivers} selected={operatorIds}
 *     onSelectedChange={setOperatorIds}
 *     onAddOperator={canInvite ? openInvite : undefined} />
 *
 * The whole row toggles, not only the box, because a thumb lands on the
 * name. Creating a call ticks the member who is creating it by default;
 * that is the screen's choice, made in `selected`.
 *
 * The ticks are the sheet's own until Done, which hands them over in one
 * `onSelectedChange`. Passing each tick straight through redrew the form
 * behind the sheet a chip at a time, and the page jumped under it with
 * every name. Shut any other way, a swipe or a tap outside, and the ticks
 * are dropped, as a Cancel would.
 */
export function OperatorPicker({
  open,
  onOpenChange,
  operators,
  selected,
  onSelectedChange,
  onAddOperator,
  title = 'Operators',
}: OperatorPickerProps) {
  const [draft, setDraft] = React.useState(selected);
  // Each opening starts from what is on the call, set while rendering
  // rather than in an effect, so the sheet never opens on last time's ticks.
  const [wasOpen, setWasOpen] = React.useState(open);
  if (open !== wasOpen) {
    setWasOpen(open);
    if (open) setDraft(selected);
  }
  const chosen = new Set(draft);
  const toggle = (id: string) =>
    setDraft((current) =>
      current.includes(id)
        ? current.filter((other) => other !== id)
        : [...current, id]
    );

  return (
    <BottomSheet open={open} onOpenChange={onOpenChange}>
      <BottomSheetContent
        title={title}
        // Done is the way out, as the Figma's sheet has it: no close beside
        // the title. A swipe or a tap outside still shuts it.
        hideCloseButton
        ruledFooter
        footer={
          <BottomSheetClose asChild>
            <Button size="xl" pill onClick={() => onSelectedChange(draft)}>
              Done
            </Button>
          </BottomSheetClose>
        }
      >
        {/* No rules between people, as the Figma's Operators sheet lists
            them: the avatars already mark each row. 16 between rows. */}
        <Stack gap={0}>
          {operators.length === 0 ? (
            <EmptyState size="sm" title="No one to put on the call yet" />
          ) : null}
          {operators.map((operator) => (
            <MemberRow
              key={operator.id}
              size="lg"
              inset="between"
              name={operator.name}
              avatarSrc={operator.avatarSrc}
              detail={operator.detail}
              interactive
              onClick={() => toggle(operator.id)}
              control={
                <Checkbox
                  size="lg"
                  checked={chosen.has(operator.id)}
                  onCheckedChange={() => toggle(operator.id)}
                  // The row toggles too; one tap on the box is one toggle.
                  onClick={(event) => event.stopPropagation()}
                  aria-label={operator.name}
                />
              }
            />
          ))}
          {onAddOperator ? (
            <ListRow
              as="button"
              interactive
              inset="between"
              onClick={onAddOperator}
            >
              <Inline gap={2} align="center" grow>
                <IconTile icon={Plus} size={8} shape="circle" />
                {/* Half strength, as drawn: an action beside the people,
                    not one more of them. */}
                <Text as="span" variant="h2" tone="muted">
                  Add new operator
                </Text>
              </Inline>
            </ListRow>
          ) : null}
        </Stack>
      </BottomSheetContent>
    </BottomSheet>
  );
}
